import { useEffect, useRef, useState } from "react";
import { B, fonts, Input, Btn, Card, ImageUpload } from "../../../components/UI";
import { generateUpiQrImage } from "../../../utils/upiQr";

// Branding tab is isolated for easier maintenance of brand settings and assets.
export default function BrandingTab({
  brandForm,
  setBrandForm,
  logoPreview,
  setLogoFile,
  setLogoPreview,
  handleBrandSave,
  brandSaving,
}) {
  // ── Local payment-QR preview (tests image generation without sending) ──────
  const [qrAmount, setQrAmount] = useState("499.00");
  const [qrInvoice, setQrInvoice] = useState("INV-TEST-1234");
  const [qrPreviewUrl, setQrPreviewUrl] = useState("");
  const [qrPreviewError, setQrPreviewError] = useState("");
  const [qrPreviewBusy, setQrPreviewBusy] = useState(false);
  const qrUrlRef = useRef("");

  const releaseQrPreview = () => {
    if (qrUrlRef.current) {
      URL.revokeObjectURL(qrUrlRef.current);
      qrUrlRef.current = "";
    }
  };
  // Revoke the object URL on unmount (read the ref directly to satisfy
  // react-hooks/exhaustive-deps without effect churn).
  useEffect(() => () => {
    if (qrUrlRef.current) URL.revokeObjectURL(qrUrlRef.current);
  }, []);

  const handleQrPreview = async () => {
    setQrPreviewBusy(true);
    setQrPreviewError("");
    try {
      // Uses the CURRENT (possibly unsaved) form values so VPA/name/logo edits
      // can be iterated on without saving first.
      const blob = await generateUpiQrImage({
        amount: qrAmount,
        invoiceNumber: qrInvoice,
        branding: brandForm,
      });
      const url = URL.createObjectURL(blob);
      releaseQrPreview();
      qrUrlRef.current = url;
      setQrPreviewUrl(url);
    } catch (e) {
      console.error("[BrandingTab] QR preview error:", e);
      setQrPreviewError(e.message || "Failed to generate the QR preview");
    } finally {
      setQrPreviewBusy(false);
    }
  };

  if (!brandForm) return null;

  return (
    <div>
      <p style={{ margin: "0 0 12px", fontSize: 12, color: B.textLight, fontFamily: fonts.body }}>Logo, brand name, colors, contact info</p>
      <Card>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" }}>
          <Input label="Brand Name" value={brandForm.brandName || ""} onChange={e => setBrandForm(f => ({ ...f, brandName: e.target.value }))} />
          <Input label="Tagline" value={brandForm.tagline || ""} onChange={e => setBrandForm(f => ({ ...f, tagline: e.target.value }))} />
          <Input label="WhatsApp (with country code)" value={brandForm.whatsapp || ""} onChange={e => setBrandForm(f => ({ ...f, whatsapp: e.target.value }))} placeholder="919876543210" />
          <Input label="Phone Number" value={brandForm.phone || ""} onChange={e => setBrandForm(f => ({ ...f, phone: e.target.value }))} />
          <Input label="Owner GST Number" value={brandForm.ownerGstNumber || ""} onChange={e => setBrandForm(f => ({ ...f, ownerGstNumber: e.target.value.toUpperCase() }))} />
          <Input label="Bank Name" value={brandForm.bankName || ""} onChange={e => setBrandForm(f => ({ ...f, bankName: e.target.value }))} />
          <Input label="IFSC Code" value={brandForm.bankIfscCode || ""} onChange={e => setBrandForm(f => ({ ...f, bankIfscCode: e.target.value }))} />
          <Input label="Account Number" value={brandForm.bankAccountNumber || ""} onChange={e => setBrandForm(f => ({ ...f, bankAccountNumber: e.target.value }))} />
          <Input label="UPI ID (VPA)" value={brandForm.upiId || ""} onChange={e => setBrandForm(f => ({ ...f, upiId: e.target.value.trim() }))} placeholder="merchant@bank" />
          <Input label="UPI Payee Name" value={brandForm.upiName || ""} onChange={e => setBrandForm(f => ({ ...f, upiName: e.target.value }))} placeholder="Nilam Synthetics" />
          <Input label="Merchant Category Code (optional)" value={brandForm.upiMcc || ""} onChange={e => setBrandForm(f => ({ ...f, upiMcc: e.target.value.trim() }))} placeholder="e.g. 5699" />
          <div style={{ gridColumn: "1/-1", fontSize: 11, color: B.textLight, marginTop: -8, marginBottom: 8, fontFamily: fonts.body }}>
            Used to generate the dynamic payment QR embedded in invoice payment messages sent on WhatsApp.
          </div>

          {/* ── Local QR preview: exercises the real image-generation path ── */}
          <div style={{ gridColumn: "1/-1", border: `1px dashed ${B.border}`, borderRadius: 10, padding: 14, background: "#FBF9F7", marginBottom: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: B.textMid, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10, fontFamily: fonts.body }}>
              🧪 Test payment QR locally
            </div>

            <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
              <label style={{ display: "block" }}>
                <span style={{ display: "block", fontSize: 10, fontWeight: 600, color: B.textMid, marginBottom: 4, letterSpacing: "0.08em", textTransform: "uppercase", fontFamily: fonts.body }}>Amount (₹)</span>
                <input
                  value={qrAmount}
                  onChange={e => setQrAmount(e.target.value)}
                  inputMode="decimal"
                  style={{ width: 110, background: B.white, border: `1.5px solid ${B.border}`, borderRadius: 8, padding: "8px 10px", fontSize: 13, color: B.text, fontFamily: fonts.body, outline: "none" }}
                />
              </label>
              <label style={{ display: "block" }}>
                <span style={{ display: "block", fontSize: 10, fontWeight: 600, color: B.textMid, marginBottom: 4, letterSpacing: "0.08em", textTransform: "uppercase", fontFamily: fonts.body }}>Invoice no.</span>
                <input
                  value={qrInvoice}
                  onChange={e => setQrInvoice(e.target.value)}
                  style={{ width: 160, background: B.white, border: `1.5px solid ${B.border}`, borderRadius: 8, padding: "8px 10px", fontSize: 13, color: B.text, fontFamily: fonts.body, outline: "none" }}
                />
              </label>
              <Btn variant="secondary" small onClick={handleQrPreview} disabled={qrPreviewBusy}>
                {qrPreviewBusy ? "Generating…" : "Generate preview"}
              </Btn>
              {qrPreviewUrl && (
                <a
                  href={qrPreviewUrl}
                  download={`UPI_QR_${String(qrInvoice || "test").replace(/[^\w.-]+/g, "_")}.png`}
                  style={{ fontSize: 12, fontWeight: 700, color: B.orange, textDecoration: "none", padding: "6px 0", fontFamily: fonts.body }}
                >
                  ⬇ Download PNG
                </a>
              )}
            </div>

            {qrPreviewError && (
              <p style={{ margin: "10px 0 0", fontSize: 12, color: B.red, fontFamily: fonts.body }}>{qrPreviewError}</p>
            )}

            {qrPreviewUrl && (
              <div style={{ marginTop: 14, display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
                <img
                  src={qrPreviewUrl}
                  alt="UPI payment QR preview"
                  style={{ width: 172, height: 172, border: `1px solid ${B.border}`, borderRadius: 8, background: B.white }}
                />
                <div style={{ fontSize: 11, color: B.textLight, lineHeight: 1.7, fontFamily: fonts.body, maxWidth: 300 }}>
                  Generated with the <b>current form values above</b> (save Branding to persist them).
                  Scan it with GPay / PhonePe / Paytm / BHIM to verify the VPA, payee name, amount and
                  invoice note — the same code that runs before every WhatsApp QR send. Nothing is stored or sent.
                </div>
              </div>
            )}
          </div>

          <div style={{ gridColumn: "1/-1" }}>
            <Input label="Address" value={brandForm.address || ""} onChange={e => setBrandForm(f => ({ ...f, address: e.target.value }))} />
          </div>
          <Input label="Address Reel (Instagram) URL" value={brandForm.addressReelUrl || ""} onChange={e => setBrandForm(f => ({ ...f, addressReelUrl: e.target.value }))} placeholder="https://www.instagram.com/reel/..." />
          <Input label="Google Maps Location URL" value={brandForm.googleMapsUrl || ""} onChange={e => setBrandForm(f => ({ ...f, googleMapsUrl: e.target.value }))} placeholder="https://maps.app.goo.gl/..." />
          <div style={{ gridColumn: "1/-1", fontSize: 11, color: B.textLight, marginTop: -8, marginBottom: 8, fontFamily: fonts.body }}>
            Sent as three WhatsApp messages when a customer asks for the address: Instagram reel → Google Maps link → the address above.
          </div>
          <div>
            <label style={{ display: "block", fontSize: 10, fontWeight: 600, color: B.textMid, marginBottom: 8, letterSpacing: "0.1em", textTransform: "uppercase", fontFamily: fonts.body }}>Accent Color</label>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <input type="color" value={brandForm.primaryColor || B.orange}
                onChange={e => setBrandForm(f => ({ ...f, primaryColor: e.target.value }))}
                style={{ width: 44, height: 36, border: "none", borderRadius: 6, cursor: "pointer", padding: 2 }} />
              <span style={{ fontSize: 12, color: B.textMid, fontFamily: fonts.body }}>{brandForm.primaryColor || B.orange}</span>
            </div>
          </div>
        </div>
        <div style={{ marginTop: 8 }}>
          <ImageUpload
            preview={logoPreview || brandForm.logoUrl}
            onFile={f => { setLogoFile(f); setLogoPreview(URL.createObjectURL(f)); }}
            label="Brand Logo" hint="PNG with transparent background · Square" width={80} height={80} />
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
          <Btn onClick={handleBrandSave} variant="primary" disabled={brandSaving}>
            {brandSaving ? "Saving..." : "Save Branding"}
          </Btn>
        </div>
      </Card>
    </div>
  );
}
